import { formatDuration, stintDuration } from '@racedash/shared'
import { driverName } from '../screenContext'
import { Datum, NoValue, Tile } from '../Tile'
import { useDriverStints } from '../useDriverStints'
import type { WidgetProps } from './props'

/**
 * Who is in the car, for how long, and for how many laps.
 *
 * A driver stint rather than a pit stint: a stop that does not change driver —
 * fuel, a repair, a penalty — is inside it and the clock runs on through it.
 * The stints are the ones the Driver tile and the stint chart use, so the name
 * here cannot disagree with either, and a driver confirmed after a stop splits
 * the stint at that stop's pit-out however late the confirmation came.
 *
 * Separate from the Driver tile on purpose. That tile left the duration out
 * because it fought the name for space; here the duration is the headline.
 *
 * While the car is in the pits the clock is held, because whether this is a
 * driver change is not known until it comes out.
 */
export default function Stint({ car, data, now, context }: WidgetProps<'stint'>) {
  const { stints, at } = useDriverStints(car, data, context, now)
  const current = stints.at(-1) ?? null

  if (!car) {
    return (
      <Tile label="Stint">
        <div className="stint-blank">No car</div>
      </Tile>
    )
  }
  if (!current) {
    return (
      <Tile label="Stint">
        <NoValue as="time" />
      </Tile>
    )
  }

  const ms = stintDuration(current, at)
  // The roster's spelling where the stint names a roster driver, the feed's
  // own otherwise — the order the Driver tile uses.
  const name = driverName(context, current.driverId) ?? current.name ?? null
  const inPits = current.pausedAt != null && current.to == null

  const max = context.plan?.regime?.stint?.maxDurationMs ?? null
  const left = max != null ? max - ms : null
  // Ten minutes is the warning, not a rule: enough to call a driver in.
  const limit = left == null ? null : left <= 0 ? 'over' : left <= 10 * 60_000 ? 'near' : 'ok'

  return (
    <Tile label="Stint">
      <div className="stint">
        <div className="stint-who">{name ?? <span className="dash-muted">Driver not known</span>}</div>
        <Datum value={formatDuration(ms)} as="time" sub={current.laps != null ? lapsLabel(current.laps) : undefined} />
        <div className="stint-foot">
          {inPits && <span className="stint-pits">in the pits</span>}
          {max != null && left != null && (
            <span className={limit === 'ok' ? undefined : 'stint-limit'}>
              {left > 0 ? `${formatDuration(left)} left of ${formatDuration(max)}` : `${formatDuration(-left)} over ${formatDuration(max)}`}
            </span>
          )}
          {!current.declared && name && <span>from the timing</span>}
        </div>
      </div>
    </Tile>
  )
}

function lapsLabel(n: number): string {
  return n === 1 ? '1 lap' : `${n} laps`
}
